import type { DraftEntry, DraftLedger } from "../domain/draft";
import {
  authorD2LocalEnrichmentProposal,
  type D2LocalEnrichmentProposal,
  type D2LocalPropertyOperationInput,
  type D2LocalPropertyOperationKind,
  type D2LocalPropertyState
} from "../domain/d2-local-enrichment-proposal";
import {
  authorD2LocalClassificationProposal,
  type D2LocalClassificationProposal,
  type D2LocalClassificationReference,
  type D2LocalClassificationTargetInput
} from "../domain/d2-local-classification-proposal";
import type { AnonymousFixture } from "../domain/types";
import type { LocalIfcObservation } from "./local-ifc";

/**
 * A local IFC source receipt is only usable to bind a proposal when the active observation
 * actually succeeded. A `null` observation or a `refused` observation carries no trustworthy
 * Plan/fixture pairing, so binding must be explicit and no digest may be fabricated.
 */
export type LocalSourceBinding =
  | { readonly bound: false }
  | { readonly bound: true; readonly source: { readonly byteLength: number; readonly sha256: string } };

export type LocalProposalBinding<Proposal> =
  | { readonly bound: false }
  | { readonly bound: true; readonly proposal: Proposal };

export function localSourceBinding(observation: LocalIfcObservation | null): LocalSourceBinding {
  if (observation === null || observation.state !== "observed") return { bound: false };
  return {
    bound: true,
    source: { byteLength: observation.source.byteLength, sha256: observation.source.sha256 }
  };
}

function primitiveValue(value: unknown): string | number | boolean | undefined {
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean" ? value : undefined;
}

/** The exact single-key sentinel `draft.ts` uses to record "this property did not exist before staging". */
function isMissingSentinel(value: unknown): value is { readonly state: "missing" } {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value as Record<string, unknown>).length === 1 &&
    (value as Record<string, unknown>).state === "missing"
  );
}

function draftBeforeState(before: unknown): D2LocalPropertyState {
  return isMissingSentinel(before) ? { state: "missing" } : { state: "present", value: structuredClone(before) };
}

/**
 * Converts every Draft ledger entry into a `workbench-local-enrichment-proposal/1` target,
 * regardless of the entry's `source` (property, code, or component). Entries are never dropped
 * here: a code- or component-sourced entry becomes a property operation on the same element/
 * property pair the ledger already tracked, so it is accounted for exactly once by the
 * enrichment proposal's target/writer closure instead of being silently excluded.
 *
 * `replace` becomes `property-add` when the ledger's recorded before state is missing and
 * `property-replace` when it is present; `clear`/`remove` map to the identically named kinds.
 * The recorded before is passed through unchanged (never re-derived from the current fixture),
 * so a ledger entry that has drifted from the active fixture is caught as an explicit
 * `before-mismatch` refusal by the authoring function instead of being silently reconciled.
 */
export function draftEntriesToEnrichmentTargets(
  entries: readonly DraftEntry[]
): readonly D2LocalPropertyOperationInput[] {
  return entries.map((entry) => {
    const before = draftBeforeState(entry.before);
    if (entry.operation === "clear") {
      return { kind: "property-clear", elementId: entry.elementId, property: entry.property, before };
    }
    if (entry.operation === "remove") {
      return { kind: "property-remove", elementId: entry.elementId, property: entry.property, before };
    }
    const kind: D2LocalPropertyOperationKind = before.state === "missing" ? "property-add" : "property-replace";
    return { kind, elementId: entry.elementId, property: entry.property, before, value: primitiveValue(entry.after) };
  });
}

export async function authorLocalEnrichmentProposalFromDraft(
  fixture: AnonymousFixture,
  ledger: DraftLedger,
  binding: LocalSourceBinding
): Promise<LocalProposalBinding<D2LocalEnrichmentProposal>> {
  if (!binding.bound) return { bound: false };
  const targets = draftEntriesToEnrichmentTargets(ledger.entries);
  const proposal = await authorD2LocalEnrichmentProposal({ fixture, source: binding.source, targets });
  return { bound: true, proposal };
}

/**
 * Converts staged Codes classification edits (elementId -> proposed reference) into
 * `workbench-local-classification-proposal/1` targets. `before` is always re-read from the
 * active fixture's current `classification` field, never from caller-supplied state, so a stale
 * staged edit is caught as a `before-mismatch` refusal by the authoring function instead of
 * silently overwriting a classification that changed since it was staged.
 */
export function classificationTargetsFromStaged(
  fixture: AnonymousFixture,
  staged: ReadonlyMap<string, D2LocalClassificationReference>
): readonly D2LocalClassificationTargetInput[] {
  return [...staged.entries()].map(([elementId, after]) => {
    const element = fixture.elements.find((candidate) => candidate.id === elementId);
    return { elementId, before: element === undefined ? null : structuredClone(element.classification), after };
  });
}

export async function authorLocalClassificationProposalFromStaged(
  fixture: AnonymousFixture,
  staged: ReadonlyMap<string, D2LocalClassificationReference>,
  binding: LocalSourceBinding
): Promise<LocalProposalBinding<D2LocalClassificationProposal>> {
  if (!binding.bound) return { bound: false };
  const targets = classificationTargetsFromStaged(fixture, staged);
  const proposal = await authorD2LocalClassificationProposal({ fixture, source: binding.source, targets });
  return { bound: true, proposal };
}
